// =============================================
// 3D CARBURETOR MODEL — TEXTBOOK-ACCURATE
// Vertical downdraft orientation matching
// Jeppesen FIG 07-39/42/44 and Avotek 2-4-6
// =============================================
import * as THREE from 'three';
import {
    housingMaterial, steelMaterial, brassMaterial, shaftMaterial,
    fuelSupplyMaterial, regulatedFuelMaterial, meteredFuelMaterial,
    impactAirMaterial, venturiSuctionMaterial, rubberMaterial,
    TEXTBOOK_COLORS
} from './materials.js';

// ===== HELPERS =====

function tag(mesh, partId, system, layer = 1, extra = {}) {
    mesh.userData = {
        partId, system, layer,
        internal: layer > 1,
        housing: extra.housing || false,
        passageType: extra.passageType || null,
        ...extra,
    };
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    return mesh;
}

/** Build a smooth tube from an array of Vector3 waypoints */
function makeTube(points, radius, segments, material) {
    const curve = new THREE.CatmullRomCurve3(points);
    const geo = new THREE.TubeGeometry(curve, segments || 32, radius, 12, false);
    return new THREE.Mesh(geo, material);
}

/** Build a lathe profile from an array of {r, y} objects */
function makeLathe(profile, segments = 48) {
    const pts = profile.map(p => new THREE.Vector2(p.r, p.y));
    return new THREE.LatheGeometry(pts, segments);
}

// ===== MAIN BUILD FUNCTION =====
// Orientation: Y-axis is UP. Air enters from BOTTOM (negative Y), exits TOP.
// This matches the updraft carburetor layout (Marvel-Schebler MA-4SPA)
// which is standard for light aircraft engines.

export function buildCarburetorModel() {
    const carb = new THREE.Group();
    carb.name = 'carburetor';

    // Dimensions (in arbitrary units, ~1 unit = ~1 inch scale)
    const barrelR = 1.1;      // barrel outer radius
    const barrelH = 3.2;      // barrel height (vertical)
    const wallT = 0.1;        // wall thickness
    const bowlW = 2.6;        // float bowl width
    const bowlD = 2.0;        // float bowl depth (front-back)
    const bowlH = 1.8;        // float bowl height

    // ====================================================================
    //  AIR BARREL — vertical cylinder, air flows top-to-bottom
    // ====================================================================
    const barrelProfile = [];
    for (let i = 0; i <= 40; i++) {
        const t = i / 40;
        const y = t * barrelH; // top = barrelH, bottom = 0
        // Flare at bottom (air inlet) and top (flange)
        let r = barrelR;
        if (t < 0.08) r += 0.15 * (1 - t / 0.08);      // bottom inlet flare
        if (t > 0.92) r += 0.12 * ((t - 0.92) / 0.08);  // top outlet flange
        barrelProfile.push({ r, y });
    }
    const barrelGeo = makeLathe(barrelProfile, 48);
    const barrel = new THREE.Mesh(barrelGeo, housingMaterial());
    barrel.position.set(0, 0, 0);
    tag(barrel, 'carb-body', 'air', 1, { housing: true });
    carb.add(barrel);

    // Reinforcement rings on barrel
    [0.25, 0.5, 0.8].forEach(frac => {
        const ringY = frac * barrelH;
        const ringGeo = new THREE.TorusGeometry(barrelR + 0.02, 0.03, 8, 48);
        const ring = new THREE.Mesh(ringGeo, housingMaterial());
        ring.position.set(0, ringY, 0);
        ring.rotation.x = Math.PI / 2;
        tag(ring, 'carb-body', 'air', 1, { housing: true });
        carb.add(ring);
    });

    // ====================================================================
    //  VENTURI (inside barrel) — Gaussian constriction
    // ====================================================================
    const venturiProfile = [];
    const throatY = barrelH * 0.55; // throat at 55% from bottom
    for (let i = 0; i <= 40; i++) {
        const t = i / 40;
        const y = t * barrelH;
        const dist = (y - throatY) / (barrelH * 0.25);
        const gaussian = Math.exp(-dist * dist);
        const r = (barrelR - wallT) * (1.0 - 0.4 * gaussian);
        venturiProfile.push({ r, y });
    }
    const venturiGeo = makeLathe(venturiProfile, 48);
    const venturi = new THREE.Mesh(venturiGeo, venturiSuctionMaterial());
    tag(venturi, 'venturi', 'air', 2, { internal: true, passageType: 'venturiSuction' });
    carb.add(venturi);

    // Throat ring indicator
    const throatRing = new THREE.Mesh(
        new THREE.TorusGeometry(0.55, 0.025, 12, 48),
        (() => { const m = venturiSuctionMaterial(); m.emissiveIntensity = 0.3; m.opacity = 0.5; return m; })()
    );
    throatRing.position.set(0, throatY, 0);
    throatRing.rotation.x = Math.PI / 2;
    tag(throatRing, 'venturi', 'air', 2, { internal: true });
    carb.add(throatRing);

    // ====================================================================
    //  BOOSTER VENTURI (smaller, inside main venturi)
    // ====================================================================
    const boosterProfile = [];
    for (let i = 0; i <= 24; i++) {
        const t = i / 24;
        const y = throatY - 0.4 + t * 0.8;
        const dist = (y - throatY) / 0.2;
        const g = Math.exp(-dist * dist);
        const r = 0.22 * (1.0 - 0.4 * g);
        boosterProfile.push({ r, y });
    }
    const boosterGeo = makeLathe(boosterProfile, 32);
    const boosterMat = housingMaterial(); boosterMat.color.set(0x7A8A9F);
    const booster = new THREE.Mesh(boosterGeo, boosterMat);
    tag(booster, 'venturi', 'air', 2, { internal: true });
    carb.add(booster);

    // Booster support struts
    for (let a = 0; a < 2; a++) {
        const angle = a * Math.PI;
        const strut = new THREE.Mesh(
            new THREE.BoxGeometry(0.35, 0.03, 0.03),
            housingMaterial()
        );
        strut.position.set(Math.cos(angle) * 0.42, throatY, Math.sin(angle) * 0.42);
        strut.rotation.y = angle;
        tag(strut, 'venturi', 'air', 2, { internal: true });
        carb.add(strut);
    }

    // ====================================================================
    //  THROTTLE VALVE (butterfly) — above venturi
    // ====================================================================
    const throttleY = barrelH * 0.85;
    const throttlePlate = new THREE.Mesh(
        new THREE.CylinderGeometry(barrelR - wallT - 0.05, barrelR - wallT - 0.05, 0.02, 48),
        steelMaterial()
    );
    throttlePlate.position.set(0, throttleY, 0);
    throttlePlate.rotation.z = 0.4; // partially open
    tag(throttlePlate, 'throttle-valve', 'control', 2, { internal: true });
    carb.add(throttlePlate);

    // Throttle shaft (horizontal, through barrel wall)
    const tShaft = new THREE.Mesh(
        new THREE.CylinderGeometry(0.035, 0.035, barrelR * 2 + 0.6, 12),
        shaftMaterial()
    );
    tShaft.position.set(0, throttleY, 0);
    tShaft.rotation.z = Math.PI / 2;
    tag(tShaft, 'throttle-shaft', 'linkage', 2, { internal: true });
    carb.add(tShaft);

    // Throttle lever (external, on right side)
    const lever = new THREE.Mesh(
        new THREE.BoxGeometry(0.05, 0.55, 0.04),
        brassMaterial()
    );
    lever.position.set(barrelR + 0.35, throttleY + 0.15, 0);
    tag(lever, 'throttle-valve', 'control', 1);
    carb.add(lever);

    // ====================================================================
    //  FLOAT BOWL — wider box below the barrel
    // ====================================================================
    // Outer shell
    const bowlShape = new THREE.Shape();
    const hw = bowlW / 2, hd = bowlD / 2, cr = 0.12;
    bowlShape.moveTo(-hw + cr, -hd);
    bowlShape.lineTo(hw - cr, -hd);
    bowlShape.quadraticCurveTo(hw, -hd, hw, -hd + cr);
    bowlShape.lineTo(hw, hd - cr);
    bowlShape.quadraticCurveTo(hw, hd, hw - cr, hd);
    bowlShape.lineTo(-hw + cr, hd);
    bowlShape.quadraticCurveTo(-hw, hd, -hw, hd - cr);
    bowlShape.lineTo(-hw, -hd + cr);
    bowlShape.quadraticCurveTo(-hw, -hd, -hw + cr, -hd);

    const bowlGeo = new THREE.ExtrudeGeometry(bowlShape, {
        depth: bowlH, bevelEnabled: true, bevelThickness: 0.04,
        bevelSize: 0.04, bevelSegments: 3,
    });
    bowlGeo.rotateX(-Math.PI / 2);
    bowlGeo.translate(0, -bowlH / 2, 0);
    const bowlMesh = new THREE.Mesh(bowlGeo, housingMaterial());
    bowlMesh.position.set(0, -0.1, 0);
    tag(bowlMesh, 'float-bowl', 'fuel', 1, { housing: true });
    carb.add(bowlMesh);

    // Bowl cover / gasket flange (top of bowl)
    const coverGeo = new THREE.BoxGeometry(bowlW + 0.1, 0.06, bowlD + 0.1);
    const cover = new THREE.Mesh(coverGeo, housingMaterial());
    cover.position.set(0, -0.05, 0);
    tag(cover, 'float-bowl', 'fuel', 1, { housing: true });
    carb.add(cover);

    // Cover bolts (6)
    for (let i = 0; i < 6; i++) {
        const angle = (i / 6) * Math.PI * 2;
        const bolt = new THREE.Mesh(
            new THREE.CylinderGeometry(0.04, 0.04, 0.05, 6),
            steelMaterial()
        );
        bolt.position.set(Math.cos(angle) * 1.1, -0.02, Math.sin(angle) * 0.8);
        tag(bolt, 'float-bowl', 'fuel', 1);
        carb.add(bolt);
    }

    // ====================================================================
    //  FUEL LEVEL — translucent yellow fill inside bowl
    // ====================================================================
    const fuelLevelGeo = new THREE.BoxGeometry(bowlW - 0.3, bowlH * 0.4, bowlD - 0.3);
    const fuelLevel = new THREE.Mesh(fuelLevelGeo, fuelSupplyMaterial());
    fuelLevel.position.set(0, -bowlH * 0.55, 0);
    tag(fuelLevel, 'float-bowl', 'fuel', 2, { internal: true, passageType: 'fuelSupply' });
    carb.add(fuelLevel);

    // ====================================================================
    //  FLOAT — dual pontoon on hinged arm
    // ====================================================================
    const floatMat = brassMaterial(); floatMat.color.set(0xD4A853); floatMat.roughness = 0.5;

    const pontoonL = new THREE.Mesh(new THREE.CapsuleGeometry(0.14, 0.5, 8, 12), floatMat);
    pontoonL.position.set(-0.45, -bowlH * 0.5, 0);
    pontoonL.rotation.z = Math.PI / 2;
    tag(pontoonL, 'float', 'linkage', 2, { internal: true });
    carb.add(pontoonL);

    const pontoonR = new THREE.Mesh(new THREE.CapsuleGeometry(0.14, 0.5, 8, 12), floatMat.clone());
    pontoonR.position.set(0.45, -bowlH * 0.5, 0);
    pontoonR.rotation.z = Math.PI / 2;
    tag(pontoonR, 'float', 'linkage', 2, { internal: true });
    carb.add(pontoonR);

    // Float arm
    const arm = new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.03, 0.06), shaftMaterial());
    arm.position.set(0, -bowlH * 0.35, 0);
    tag(arm, 'float', 'linkage', 2, { internal: true });
    carb.add(arm);

    // Hinge bracket + pin
    const hinge = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.08, 0.1), housingMaterial());
    hinge.position.set(0, -0.25, 0.15);
    tag(hinge, 'float', 'linkage', 2, { internal: true });
    carb.add(hinge);

    // ====================================================================
    //  NEEDLE VALVE & SEAT — at top of bowl
    // ====================================================================
    const needle = new THREE.Mesh(new THREE.ConeGeometry(0.045, 0.3, 16), shaftMaterial());
    needle.position.set(0.6, -0.05, -0.3);
    tag(needle, 'needle-valve', 'fuel', 2, { internal: true });
    carb.add(needle);

    const seat = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.08, 0.1, 16), brassMaterial());
    seat.position.set(0.6, -0.18, -0.3);
    tag(seat, 'needle-valve', 'fuel', 2, { internal: true });
    carb.add(seat);

    // ====================================================================
    //  FUEL INLET — horizontal tube into bowl side
    // ====================================================================
    const fuelInlet = new THREE.Mesh(
        new THREE.CylinderGeometry(0.12, 0.12, 0.7, 16),
        housingMaterial()
    );
    fuelInlet.position.set(bowlW / 2 + 0.35, -0.3, -0.3);
    fuelInlet.rotation.z = Math.PI / 2;
    tag(fuelInlet, 'fuel-inlet', 'fuel', 1);
    carb.add(fuelInlet);

    // Hex nut fitting
    const hexNut = new THREE.Mesh(
        new THREE.CylinderGeometry(0.16, 0.16, 0.1, 6),
        brassMaterial()
    );
    hexNut.position.set(bowlW / 2 + 0.7, -0.3, -0.3);
    hexNut.rotation.z = Math.PI / 2;
    tag(hexNut, 'fuel-inlet', 'fuel', 1);
    carb.add(hexNut);

    // Strainer screen
    const strainer = new THREE.Mesh(
        new THREE.CylinderGeometry(0.09, 0.09, 0.18, 16),
        new THREE.MeshStandardMaterial({ color: 0xAA8833, wireframe: true, transparent: true, opacity: 0.45 })
    );
    strainer.position.set(bowlW / 2 + 0.05, -0.3, -0.3);
    strainer.rotation.z = Math.PI / 2;
    tag(strainer, 'fuel-strainer', 'fuel', 2, { internal: true });
    carb.add(strainer);

    // ====================================================================
    //  MAIN METERING JET (inside bowl, below barrel center)
    // ====================================================================
    const mainJet = new THREE.Mesh(
        new THREE.CylinderGeometry(0.06, 0.04, 0.18, 12),
        brassMaterial()
    );
    mainJet.position.set(0, -0.35, 0);
    tag(mainJet, 'main-jet', 'fuel', 3, { internal: true, passageType: 'regulatedFuel' });
    carb.add(mainJet);

    // ====================================================================
    //  MAIN DISCHARGE NOZZLE — vertical tube from jet up through venturi
    // ====================================================================
    const nozzlePath = [
        new THREE.Vector3(0, -0.3, 0),
        new THREE.Vector3(0, 0.2, 0),
        new THREE.Vector3(0, 0.6, 0),
        new THREE.Vector3(0, 1.0, 0),
        new THREE.Vector3(0, throatY, 0),  // tip at venturi throat
    ];
    const nozzle = makeTube(nozzlePath, 0.035, 20, meteredFuelMaterial());
    tag(nozzle, 'main-discharge-nozzle', 'mixture', 3, { internal: true, passageType: 'meteredFuel' });
    carb.add(nozzle);

    // Nozzle tip visible in venturi
    const nozzleTip = new THREE.Mesh(new THREE.SphereGeometry(0.05, 12, 12), (() => {
        const m = meteredFuelMaterial(); m.emissiveIntensity = 0.25; return m;
    })());
    nozzleTip.position.set(0, throatY, 0);
    tag(nozzleTip, 'main-discharge-nozzle', 'mixture', 3, { internal: true, passageType: 'meteredFuel' });
    carb.add(nozzleTip);

    // ====================================================================
    //  MAIN FUEL PASSAGE — from bowl to jet
    // ====================================================================
    const mainFuelTube = makeTube([
        new THREE.Vector3(0.0, -bowlH * 0.6, 0),
        new THREE.Vector3(0.0, -bowlH * 0.4, 0),
        new THREE.Vector3(0.0, -0.35, 0),
    ], 0.03, 12, fuelSupplyMaterial());
    tag(mainFuelTube, 'main-jet', 'fuel', 3, { internal: true, passageType: 'fuelSupply' });
    carb.add(mainFuelTube);

    // ====================================================================
    //  IDLE SYSTEM
    // ====================================================================
    // Idle jet
    const idleJet = new THREE.Mesh(
        new THREE.CylinderGeometry(0.03, 0.02, 0.1, 10),
        brassMaterial()
    );
    idleJet.position.set(0.35, -0.3, 0.3);
    tag(idleJet, 'idle-jet', 'fuel', 3, { internal: true });
    carb.add(idleJet);

    // Idle passage — from bowl up to just below throttle plate
    const idleFuelTube = makeTube([
        new THREE.Vector3(0.15, -bowlH * 0.5, 0.2),
        new THREE.Vector3(0.25, -0.3, 0.25),
        new THREE.Vector3(0.35, -0.3, 0.3),   // at idle jet
        new THREE.Vector3(0.5, 0.2, 0.35),
        new THREE.Vector3(0.55, throttleY - 0.1, 0.4),
        new THREE.Vector3(0.6, throttleY + 0.05, 0.42),  // idle discharge port
    ], 0.02, 24, meteredFuelMaterial());
    tag(idleFuelTube, 'idle-port', 'mixture', 3, { internal: true, passageType: 'meteredFuel' });
    carb.add(idleFuelTube);

    // Idle discharge port (visible on barrel wall)
    const idlePort = new THREE.Mesh(
        new THREE.CylinderGeometry(0.02, 0.02, 0.08, 8),
        meteredFuelMaterial()
    );
    idlePort.position.set(0.6, throttleY + 0.05, 0.42);
    tag(idlePort, 'idle-port', 'mixture', 3, { internal: true, passageType: 'meteredFuel' });
    carb.add(idlePort);

    // Progression holes (3)
    for (let i = 0; i < 3; i++) {
        const hole = new THREE.Mesh(new THREE.SphereGeometry(0.015, 8, 8), meteredFuelMaterial());
        hole.position.set(0.58, throttleY + 0.12 + i * 0.06, 0.4);
        tag(hole, 'progression-holes', 'mixture', 3, { internal: true, passageType: 'meteredFuel' });
        carb.add(hole);
    }

    // Idle mixture screw (external, threaded brass)
    const idleMix = new THREE.Mesh(
        new THREE.CylinderGeometry(0.03, 0.02, 0.4, 12),
        brassMaterial()
    );
    idleMix.position.set(barrelR + 0.15, throttleY, 0.4);
    idleMix.rotation.z = Math.PI / 2;
    tag(idleMix, 'idle-port', 'control', 1);
    carb.add(idleMix);

    // ====================================================================
    //  AIR BLEEDS (main + idle) — tubes from top of barrel down
    // ====================================================================
    const mainBleed = makeTube([
        new THREE.Vector3(-0.15, barrelH - 0.1, -0.2),
        new THREE.Vector3(-0.12, barrelH * 0.7, -0.12),
        new THREE.Vector3(-0.05, barrelH * 0.45, -0.05),
        new THREE.Vector3(0, 0.3, 0),
    ], 0.016, 20, impactAirMaterial());
    tag(mainBleed, 'main-air-bleed', 'air', 3, { internal: true, passageType: 'impactAir' });
    carb.add(mainBleed);

    const idleBleed = makeTube([
        new THREE.Vector3(0.2, barrelH - 0.1, 0.15),
        new THREE.Vector3(0.25, barrelH * 0.65, 0.2),
        new THREE.Vector3(0.3, barrelH * 0.4, 0.25),
        new THREE.Vector3(0.35, 0.2, 0.3),
    ], 0.014, 20, impactAirMaterial());
    tag(idleBleed, 'idle-air-bleed', 'air', 3, { internal: true, passageType: 'impactAir' });
    carb.add(idleBleed);

    // Air bleed orifice fittings (brass, visible at top of barrel)
    const mainBleedOrifice = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.06, 8), brassMaterial());
    mainBleedOrifice.position.set(-0.15, barrelH + 0.02, -0.2);
    tag(mainBleedOrifice, 'main-air-bleed', 'air', 1);
    carb.add(mainBleedOrifice);

    const idleBleedOrifice = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.025, 0.06, 8), brassMaterial());
    idleBleedOrifice.position.set(0.2, barrelH + 0.02, 0.15);
    tag(idleBleedOrifice, 'idle-air-bleed', 'air', 1);
    carb.add(idleBleedOrifice);

    // ====================================================================
    //  ECONOMIZER / POWER ENRICHMENT — per FIG 2-4-21 (back-suction)
    // ====================================================================
    const econTube = makeTube([
        new THREE.Vector3(-0.35, throatY + 0.3, 0.1),   // venturi suction tap
        new THREE.Vector3(-0.45, throatY, 0.12),
        new THREE.Vector3(-0.5, 0.4, 0.15),
        new THREE.Vector3(-0.45, 0.1, 0.12),             // economizer valve
        new THREE.Vector3(-0.35, -0.2, 0.08),            // connects to fuel
    ], 0.02, 24, regulatedFuelMaterial());
    tag(econTube, 'economizer', 'fuel', 3, { internal: true, passageType: 'regulatedFuel' });
    carb.add(econTube);

    const econValve = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.08, 0.15, 12), brassMaterial());
    econValve.position.set(-0.45, 0.1, 0.12);
    tag(econValve, 'economizer', 'fuel', 2, { internal: true });
    carb.add(econValve);

    // ====================================================================
    //  ACCELERATOR PUMP — per FIG 07-44
    // ====================================================================
    // Pump cylinder (outside the bowl, to the right)
    const pumpCyl = new THREE.Mesh(
        new THREE.CylinderGeometry(0.16, 0.16, 0.4, 16),
        housingMaterial()
    );
    pumpCyl.position.set(bowlW / 2 - 0.2, -0.5, -0.6);
    tag(pumpCyl, 'accel-pump', 'linkage', 2, { internal: true });
    carb.add(pumpCyl);

    // Piston
    const piston = new THREE.Mesh(new THREE.CylinderGeometry(0.14, 0.14, 0.02, 16), rubberMaterial());
    piston.position.set(bowlW / 2 - 0.2, -0.4, -0.6);
    tag(piston, 'accel-pump', 'linkage', 2, { internal: true });
    carb.add(piston);

    // Piston rod
    const pRod = new THREE.Mesh(new THREE.CylinderGeometry(0.015, 0.015, 0.7, 8), shaftMaterial());
    pRod.position.set(bowlW / 2 - 0.2, -0.1, -0.6);
    tag(pRod, 'accel-pump', 'linkage', 2, { internal: true });
    carb.add(pRod);

    // Spring (helix)
    const springPts = [];
    for (let i = 0; i <= 80; i++) {
        const t = i / 80;
        springPts.push(new THREE.Vector3(
            Math.cos(t * 8 * Math.PI * 2) * 0.06,
            -0.7 + t * 0.28,
            Math.sin(t * 8 * Math.PI * 2) * 0.06
        ));
    }
    const springTube = makeTube(springPts, 0.006, 64, shaftMaterial());
    springTube.position.set(bowlW / 2 - 0.2, 0, -0.6);
    tag(springTube, 'accel-pump', 'linkage', 2, { internal: true });
    carb.add(springTube);

    // Check valve balls
    const inletCheck = new THREE.Mesh(new THREE.SphereGeometry(0.035, 12, 12), steelMaterial());
    inletCheck.position.set(0.5, -0.6, -0.4);
    tag(inletCheck, 'accel-pump-inlet-valve', 'fuel', 3, { internal: true });
    carb.add(inletCheck);

    const outletCheck = new THREE.Mesh(new THREE.SphereGeometry(0.035, 12, 12), steelMaterial());
    outletCheck.position.set(0.35, throatY + 0.2, -0.25);
    tag(outletCheck, 'accel-pump-outlet-valve', 'fuel', 3, { internal: true });
    carb.add(outletCheck);

    // Accelerator pump passage
    const accelTube = makeTube([
        new THREE.Vector3(0.3, -bowlH * 0.5, -0.3),
        new THREE.Vector3(0.45, -0.6, -0.35),
        new THREE.Vector3(0.5, -0.6, -0.4),            // inlet check
        new THREE.Vector3(bowlW / 2 - 0.2, -0.5, -0.6), // pump cyl
        new THREE.Vector3(bowlW / 2 - 0.3, 0.0, -0.5),
        new THREE.Vector3(0.35, throatY + 0.2, -0.25),   // outlet check
        new THREE.Vector3(0.15, throatY + 0.1, -0.05),   // discharge into venturi
    ], 0.016, 32, fuelSupplyMaterial());
    tag(accelTube, 'accel-pump', 'fuel', 3, { internal: true, passageType: 'fuelSupply' });
    carb.add(accelTube);

    // ====================================================================
    //  MIXTURE CONTROL VALVE — rotary valve + cockpit rod
    // ====================================================================
    const mixValve = new THREE.Mesh(
        new THREE.CylinderGeometry(0.07, 0.07, 0.15, 16),
        brassMaterial()
    );
    mixValve.position.set(-0.15, -0.15, 0.12);
    mixValve.rotation.x = Math.PI / 2;
    tag(mixValve, 'mixture-control', 'control', 2, { internal: true });
    carb.add(mixValve);

    // Rod (extends left out of body)
    const mixRod = new THREE.Mesh(new THREE.CylinderGeometry(0.015, 0.015, 1.5, 8), shaftMaterial());
    mixRod.position.set(-barrelR - 0.5, -0.15, 0.12);
    mixRod.rotation.z = Math.PI / 2;
    tag(mixRod, 'mixture-control', 'control', 1);
    carb.add(mixRod);

    // Red knob
    const mixKnob = new THREE.Mesh(new THREE.SphereGeometry(0.06, 12, 12), (() => {
        const m = steelMaterial(); m.color.set(0xCC3333); return m;
    })());
    mixKnob.position.set(-barrelR - 1.25, -0.15, 0.12);
    tag(mixKnob, 'mixture-control', 'control', 1);
    carb.add(mixKnob);

    // ====================================================================
    //  BOWL VENT
    // ====================================================================
    const ventTube = makeTube([
        new THREE.Vector3(0.5, -0.2, -0.45),
        new THREE.Vector3(0.45, 0.5, -0.55),
        new THREE.Vector3(0.35, 1.5, -0.6),
        new THREE.Vector3(0.25, barrelH - 0.3, -0.5),
    ], 0.015, 16, impactAirMaterial());
    tag(ventTube, 'bowl-vent', 'air', 3, { internal: true, passageType: 'impactAir' });
    carb.add(ventTube);

    // ====================================================================
    //  MOUNTING FLANGE — bottom of barrel to intake manifold
    // ====================================================================
    const flange = new THREE.Mesh(
        new THREE.CylinderGeometry(barrelR + 0.25, barrelR + 0.25, 0.08, 48),
        housingMaterial()
    );
    flange.position.set(0, barrelH + 0.02, 0);
    tag(flange, 'mounting-flange', 'mixture', 1);
    carb.add(flange);

    // Gasket
    const gasket = new THREE.Mesh(
        new THREE.RingGeometry(barrelR - wallT, barrelR + 0.2, 48),
        rubberMaterial()
    );
    gasket.position.set(0, barrelH + 0.06, 0);
    gasket.rotation.x = -Math.PI / 2;
    tag(gasket, 'mounting-flange', 'mixture', 1);
    carb.add(gasket);

    // Flange bolts
    for (let i = 0; i < 4; i++) {
        const angle = i * Math.PI / 2 + Math.PI / 4;
        const bolt = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.045, 0.08, 6), steelMaterial());
        bolt.position.set(Math.cos(angle) * (barrelR + 0.12), barrelH + 0.02, Math.sin(angle) * (barrelR + 0.12));
        tag(bolt, 'mounting-flange', 'mixture', 1);
        carb.add(bolt);
    }

    // ====================================================================
    //  CARB HEAT VALVE — above barrel (air inlet side)
    // ====================================================================
    const heatBox = new THREE.Mesh(
        new THREE.BoxGeometry(0.8, 0.55, 0.5),
        housingMaterial()
    );
    heatBox.position.set(0, -0.3, 0);
    tag(heatBox, 'carb-heat-valve', 'control', 1);
    carb.add(heatBox);

    const heatDoor = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.35, 0.025), steelMaterial());
    heatDoor.position.set(0, -0.15, 0);
    heatDoor.rotation.x = -0.3;
    tag(heatDoor, 'carb-heat-valve', 'control', 2, { internal: true });
    carb.add(heatDoor);

    // ====================================================================
    //  DRAIN PLUG — bottom of bowl
    // ====================================================================
    const drain = new THREE.Mesh(new THREE.CylinderGeometry(0.055, 0.04, 0.12, 8), brassMaterial());
    drain.position.set(0, -bowlH - 0.2, 0.25);
    tag(drain, 'drain-plug', 'fuel', 1);
    carb.add(drain);

    // ====================================================================
    //  FUEL LINE — extends from inlet
    // ====================================================================
    const fuelLine = new THREE.Mesh(
        new THREE.CylinderGeometry(0.08, 0.08, 1.2, 12),
        steelMaterial()
    );
    fuelLine.position.set(bowlW / 2 + 1.3, -0.3, -0.3);
    fuelLine.rotation.z = Math.PI / 2;
    tag(fuelLine, 'fuel-line', 'fuel', 1);
    carb.add(fuelLine);

    return carb;
}

// ====================================================================
//  LABEL DATA — Semantic LOD System
//  Each label has:
//    pos:    label text screen offset position (3D)
//    anchor: exact 3D point on the mesh where leader line connects
//    zone:   1=Macro (assemblies), 2=Mid (components), 3=Micro (hardware)
//    group:  spatial grouping for proximity culling
// ====================================================================
// Orientation: Y=0 is the bottom of the air barrel (air inlet).
// Barrel extends UP to Y=3.2 (throttle/flange).
// Bowl extends DOWN from Y=0 to Y=-1.9.
export const LABEL_DATA = {
    // ===== ZONE 1: MACRO — Major Assemblies (visible when fully zoomed out) =====
    'carb-body':          { pos: new THREE.Vector3(2.5, 1.8, 1.2),    anchor: new THREE.Vector3(0, 1.6, 0),        zone: 1, group: 'barrel' },
    'float-bowl':         { pos: new THREE.Vector3(2.2, -1.0, 1.2),   anchor: new THREE.Vector3(0, -1.0, 0),       zone: 1, group: 'bowl' },
    'carb-heat-valve':    { pos: new THREE.Vector3(1.5, -0.6, 0.8),   anchor: new THREE.Vector3(0, 0.05, 0),       zone: 1, group: 'intake' },
    'fuel-inlet':         { pos: new THREE.Vector3(2.5, -0.3, -0.8),  anchor: new THREE.Vector3(1.68, -0.3, -0.35), zone: 1, group: 'bowl' },
    'mixture-control':    { pos: new THREE.Vector3(-2.5, -0.4, 0.4),  anchor: new THREE.Vector3(-1.88, -0.2, 0.15), zone: 1, group: 'controls' },
    'mounting-flange':    { pos: new THREE.Vector3(-1.8, 3.8, -0.8),  anchor: new THREE.Vector3(0, 3.2, 0),        zone: 1, group: 'barrel' },

    // ===== ZONE 2: MID — Functional Components (visible at medium zoom) =====
    'venturi':            { pos: new THREE.Vector3(1.8, 1.6, 1.0),    anchor: new THREE.Vector3(0, 1.76, 0),       zone: 2, group: 'barrel' },
    'throttle-valve':     { pos: new THREE.Vector3(1.8, 3.0, 0.8),    anchor: new THREE.Vector3(0, 2.7, 0),        zone: 2, group: 'barrel' },
    'float':              { pos: new THREE.Vector3(-1.8, -0.8, 0.8),  anchor: new THREE.Vector3(-1.1, -0.85, 0),   zone: 2, group: 'bowl' },
    'needle-valve':       { pos: new THREE.Vector3(1.8, -0.1, -1.0),  anchor: new THREE.Vector3(0.85, -0.15, -0.5), zone: 2, group: 'bowl' },
    'main-discharge-nozzle': { pos: new THREE.Vector3(-1.5, 1.8, -0.6), anchor: new THREE.Vector3(0, 1.76, 0),     zone: 2, group: 'barrel' },
    'economizer':         { pos: new THREE.Vector3(-2.0, 0.3, 0.6),   anchor: new THREE.Vector3(-0.48, 0.12, 0.15), zone: 2, group: 'controls' },
    'accel-pump':         { pos: new THREE.Vector3(2.0, -0.6, -1.2),  anchor: new THREE.Vector3(1.15, -0.6, -0.65), zone: 2, group: 'pump' },
    'idle-port':          { pos: new THREE.Vector3(1.8, 2.5, 1.0),    anchor: new THREE.Vector3(0.42, 2.75, 0.35),  zone: 2, group: 'barrel' },

    // ===== ZONE 3: MICRO — Hardware Details (visible only when very close) =====
    'main-jet':           { pos: new THREE.Vector3(-1.2, -0.6, -0.5), anchor: new THREE.Vector3(0, -0.65, 0),      zone: 3, group: 'bowl' },
    'idle-jet':           { pos: new THREE.Vector3(1.5, -0.45, 0.8),  anchor: new THREE.Vector3(0.38, -0.35, 0.32), zone: 3, group: 'bowl' },
    'main-air-bleed':     { pos: new THREE.Vector3(-1.2, 0.2, -0.7),  anchor: new THREE.Vector3(-0.18, -0.05, -0.22), zone: 3, group: 'intake' },
    'idle-air-bleed':     { pos: new THREE.Vector3(1.2, 0.2, 0.7),    anchor: new THREE.Vector3(0.22, -0.05, 0.18), zone: 3, group: 'intake' },
    'fuel-strainer':      { pos: new THREE.Vector3(2.2, -0.6, -1.0),  anchor: new THREE.Vector3(1.4, -0.32, -0.32), zone: 3, group: 'bowl' },
    'drain-plug':         { pos: new THREE.Vector3(0.8, -2.4, 0.8),   anchor: new THREE.Vector3(0, -1.98, 0.25),   zone: 3, group: 'bowl' },
    'progression-holes':  { pos: new THREE.Vector3(1.6, 3.2, 0.8),    anchor: new THREE.Vector3(0.55, 2.82, 0.38), zone: 3, group: 'barrel' },
};


// Legacy compat — simple {partId: Vector3} for any code still using old format
export const LABEL_POSITIONS = {};
for (const [partId, data] of Object.entries(LABEL_DATA)) {
    LABEL_POSITIONS[partId] = data.pos;
}

